import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ApiResponse, getAllContracts } from '../services/contract-services';
import { RootState } from '../store';
import { setContracts, setContractsError, setContractsLoading } from '../store/reducers/contractsSlice';

import { useStaleOnResume } from './useStaleOnResume';

// Module-level (not per-component) so concurrent callers across different
// mounted components share a single in-flight request instead of each firing
// their own network call.
let inFlightFetch: Promise<ApiResponse> | null = null;

/**
 * Single source of truth for fetching/reading contracts.
 * - De-dupes concurrent fetches across every component that calls this hook.
 * - Re-fetches automatically when the tab regains visibility after being
 *   hidden long enough for the data to be considered stale.
 */
export const useContracts = () => {
    const dispatch = useDispatch();
    const contracts = useSelector((state: RootState) => state.contracts?.contracts || []);
    const loading = useSelector((state: RootState) => state.contracts?.loading || false);
    const error = useSelector((state: RootState) => state.contracts?.error || null);
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

    const fetchContracts = useCallback(
        (showLoader: boolean = true): Promise<ApiResponse> => {
            // Every caller's loading intent is recorded even when piggybacking on an
            // already-in-flight request, so a caller that wants a loader still gets one
            // reflected in Redux regardless of which caller actually triggered the fetch.
            dispatch(setContractsLoading(showLoader));

            if (inFlightFetch) {
                return inFlightFetch;
            }

            inFlightFetch = (async () => {
                try {
                    const response = await getAllContracts(showLoader);
                    if (response?.statusCode === 200 && Array.isArray(response?.data)) {
                        dispatch(setContracts(response.data));
                    } else {
                        dispatch(setContractsError(response?.message || 'Failed to fetch contracts'));
                    }
                    return response;
                } catch (err: unknown) {
                    console.error('Error fetching contracts:', err);
                    dispatch(setContractsError(err instanceof Error ? err.message : 'Failed to fetch contracts'));
                    throw err;
                } finally {
                    inFlightFetch = null;
                }
            })();

            return inFlightFetch;
        },
        [dispatch],
    );

    const refetchOnResume = useCallback(() => fetchContracts(false), [fetchContracts]);
    useStaleOnResume(refetchOnResume, isLoggedIn);

    return { contracts, loading, error, fetchContracts };
};
